import { PlayIcon, Square } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useToggleDriveMode from "@/components/AppHeader/hooks/useToggleDriveMode";
import { cn } from "@/lib/utils";
import { CursorType } from "@/store/globalSettingsSlice";
import { DESIGN_TOOLBAR_SHORTCUTS } from "../constants/designToolbarShortcuts";

interface InteractDesignToggleProps {
  className?: string;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleClick?: () => void;
}

export const InteractDesignToggle = ({
  className,
  onMouseEnter,
  onToggleClick,
}: InteractDesignToggleProps) => {
  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const { toggleCursor } = useToggleDriveMode();
  const [showActiveState, setShowActiveState] = useState(false);

  const isInInteractMode = cursorType === CursorType.Drive;

  // Show active state immediately when entering interact mode so button grows before slide collapses
  useEffect(() => {
    if (isInInteractMode) {
      const timer = setTimeout(() => {
        setShowActiveState(true);
      }, 100); // Show quickly so button expands before toolbar collapses
      return () => clearTimeout(timer);
    } else {
      setShowActiveState(false);
    }
  }, [isInInteractMode]);
  const shortcutKey = DESIGN_TOOLBAR_SHORTCUTS.DESIGN_INTERACT_MODE.key;

  const getTooltipHtml = () => {
    if (isInInteractMode) {
      return `<div style="text-align: center;">
        <div style="font-weight: 500; margin-bottom: 2px;">Interact mode enabled</div>
        <div style="font-size: 11px; color: #a1a1aa;">Click to switch to Design mode</div>
        <div style="font-size: 10px; color: #71717a; margin-top: 4px; padding: 2px 6px; background: rgba(255,255,255,0.1); border-radius: 4px; display: inline-block;">${shortcutKey}</div>
      </div>`;
    }
    return `<div style="text-align: center;">
      <div style="font-weight: 500; margin-bottom: 2px;">Preview mode disabled</div>
      <div style="font-size: 11px; color: #a1a1aa;">Click to enable Interact mode</div>
      <div style="font-size: 10px; color: #71717a; margin-top: 4px; padding: 2px 6px; background: rgba(255,255,255,0.1); border-radius: 4px; display: inline-block;">${shortcutKey}</div>
    </div>`;
  };

  return (
    <button
      className={cn(
        "mx-1.5 px-2 py-1.5 rounded-md transition-all duration-300 ease-in-out transform relative z-20 flex items-center gap-1.5",
        showActiveState
          ? "bg-red-500 scale-105 border border-red-500 hover:bg-red-600"
          : "bg-[#6F55F2] text-white scale-100 hover:bg-[#5B44D6] border-[0.5px] border-[#6F55F2]",
        className,
      )}
      data-tooltip-id="tooltip"
      data-tooltip-html={getTooltipHtml()}
      onMouseEnter={onMouseEnter}
      type="button"
      onClick={() => {
        onToggleClick?.();
        toggleCursor();
      }}
    >
      {showActiveState ? (
        <>
          <Square className="size-3" fill="white" stroke="white" />
          <span className="text-xs font-medium text-white">
            Return to Design
          </span>
        </>
      ) : (
        <>
          <PlayIcon className="size-3" fill="none" stroke="currentColor" />
          <span className="text-xs font-medium">Interact</span>
        </>
      )}
    </button>
  );
};

export default memo(InteractDesignToggle);
